import * as React from 'react';
import {useCallback, useEffect, useMemo} from 'react';
import {Box, Grid} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useFormik} from 'formik';
import {
    useStyles,
} from './NewAccountPhoneBookContactDialog.utils';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {AccountPermission} from '../../store/types/Permission';
import DialogContainer, {DialogButton} from '../../components/AlertDialog/DialogContainer';
import CustomizedTextField from '../../components/TextField/TextField';
import { addDevicePhoneBookValidationSchema, NewAccountPhoneBookContactDialogProps } from './NewAccountPhoneBookContactDialog.utils';
import { actions } from '../../store';
import { showToast } from '../../utils/showToast';
import { APIErrorInterface } from '../../store/types';
import { PhonebookRecord } from '../../store/actions/accountPhoneBook/payloads';
import { YesNo } from '../../store/types/CallScreening';
import SelectField from '../../components/SelectField/SelectField';
import SwitchWithLabel from '../../components/SwitchWithLabel/SwitchWithLabel';
import IconWithTooltip from '../../components/Tooltip/IconWithTooltip';
import { DropDownDictionaryItem } from '../CallHistory/ActiveCalls/ActiveCalls.utils';

const NewAccountPhoneBookEntryDialog: React.FC<NewAccountPhoneBookContactDialogProps> = ({
        isOpen,
        clickOnClose,
        editRecord,
    }) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();

    const initialValues: PhonebookRecord = useMemo(
        () => (editRecord ? editRecord : {
            phone_number: '',
            i_account_phonebook: 0,
            name: '',
            phone_type: 'work',
            favorite_rating: YesNo.No,
            unlock_time: '',
            lock_type: 'None',
            dial_id: undefined
        } as PhonebookRecord),
        [editRecord],
    );

    const doSubmitDate = useCallback((form: PhonebookRecord, 
        setFieldError: (field: string, value: string | undefined) => void,
        setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => void) => {
        dispatch(actions.addEditAccountPhoneBookRecord.request({
            record: {
                ...form,
                i_account_phonebook: editRecord?.i_account_phonebook ?? 0,
                unlock_time: editRecord?.unlock_time,
                lock_type: editRecord?.lock_type ?? 'None',
            },
            callback: (error?: APIErrorInterface) => {
                if(!error)
                {
                    clickOnClose?.(true);
                }
                else
                {
                    switch(error.faultcode)
                    {
                        case 'Server.Account.incorrect_dial_id_length':
                            const match1 = error.faultstring?.match(/\((\d+)\)/);
                            const value1 = match1 ? Number(match1[1]) : 0;
                            setFieldError('dial_id', t('errors:accountPhoneBook.speedDialNumberExceedsMaximum', {
                                speed_dial_max_length: value1
                            }));
                            break;
                        case 'Server.Account.max_records_exceeded':
                            const match2 = error.faultstring?.match(/\((\d+)\)/);
                            const value2 = match2 ? Number(match2[1]) : 0;
                            showToast(t('errors:accountPhoneBook.youHaveExceededTheMaximumNumberOfPhoneBookContacts', {
                                max_contacts: value2
                            }));
                            clickOnClose?.(false);
                            break;
                        case 'Server.Account.fav_number_exists':
                            setFieldError('phone_number', t('errors:accountPhoneBook.phoneBookContactWithThisPhoneAlreadyExists'));
                            break;
                        case 'Server.Account.max_favorites_exceeded':
                            setFieldError('favorite_rating', '1');
                            setFieldValue('favorite_rating', YesNo.No);
                            const match3 = error.faultstring?.match(/\((\d+)\)/);
                            const value3 = match3 ? Number(match3[1]) : 0;
                            showToast(t('errors:accountPhoneBook.favoriteNumberLimit', {
                                max_favorites: value3
                            }));
                            break;
                        case 'Server.Account.not_matched_pattern':
                            setFieldError('phone_number', t('errors:accountPhoneBook.phoneNumberPattern'));
                            break;
                        case 'Server.Account.e164_convertion_error':
                            setFieldError('phone_number', t('errors:accountPhoneBook.phoneNumberFormat'));
                            break;
                        case 'Server.Account.dial_id_is_forbidden':
                            setFieldError('dial_id', t('errors:accountPhoneBook.speedDialIsBlocked'));
                            break;
                        case 'Server.Account.dial_id_is_used':
                            setFieldError('dial_id', t('errors:accountPhoneBook.speedDialIsInUse'));
                            break;
                        case 'Server.Account.invalid_phone_number':
                            showToast(t('errors:accountPhoneBook.phoneNumberCharacter'));
                            break;
                        default:
                            showToast(error.faultstring);
                            break;
                    }                    
                }
            }
        }));
    }, [editRecord, clickOnClose]);

    const {
        values,
        handleSubmit,
        resetForm,
        errors,
        setFieldError,
        handleChange,
        dirty,
        setFieldValue
    } = useFormik<PhonebookRecord>({
        initialValues,
        onSubmit: (form) => {
            doSubmitDate(form, setFieldError, setFieldValue);
        },
        validationSchema: addDevicePhoneBookValidationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: true
    });

    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const types = ['home', 'mobile', 'other', 'work'].map((v) => ({
        name: t(`screens:accountPhoneBook.${v}`),
        value: v,
    } as DropDownDictionaryItem<string>));

    const isDisabled = (): boolean => {
        return (
            !dirty ||
            !values.phone_number ||
            !values.phone_type)
    }
    
    return (
        <PermissionProvider permission={AccountPermission.PhoneBook.CompanyContacts.value}>
            <DialogContainer
                className={classes.modalContainer}
                isOpen={isOpen}
                dataQa={'account-phone-book-contact-add-new-modal'}
                header={editRecord
                        ? t<string>('screens:accountPhoneBook.editContactDialogTitle')
                        : t<string>('screens:accountPhoneBook.addContactDialogTitle')}
                headerClass={classes.header}
                dialogActionsButtons={[
                    <DialogButton
                        key="cancel"
                        label={t('common:cancel')}
                        onClick={() => {
                            clickOnClose?.(false);
                        }}
                    />,
                    <DialogButton
                        key="save"
                        label={t('common:save')}
                        disabled={
                            isDisabled()
                        }
                        onClick={handleSubmit}
                        dataQa={'account-phone-book-contact-save-button'}
                        primary
                    />,
                ]}
            >
                <form
                    className={classes.form}
                    autoComplete="off"
                    data-testid="account-phone-book-contact-form"
                >
                    <Grid className={classes.itemsContainer}>
                        <Box className={classes.boxRow}>
                            <CustomizedTextField
                                id="dial_id"
                                label={t('screens:accountPhoneBook.speedDial')}
                                value={values.dial_id}
                                helperText={
                                    errors.dial_id && errors.dial_id.length > 0
                                        ? errors.dial_id
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={15}
                                dataQa="account-phone-book-speed-dial-field"
                            />
                            <CustomizedTextField
                                id="phone_number"
                                label={t('screens:accountPhoneBook.phoneNumber')}
                                value={values.phone_number}
                                helperText={
                                    errors.phone_number && errors.phone_number.length > 0
                                        ? errors.phone_number
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={128}
                                dataQa="account-phone-book-phone-number-field"
                                required
                                disabled={values.lock_type == 'Number'}
                            />
                            <CustomizedTextField
                                id="name"
                                label={t('common:name')}
                                value={values.name}
                                helperText={
                                    errors.name && errors.name.length > 0
                                        ? errors.name
                                        : ''
                                }
                                setFieldError={setFieldError}
                                onChange={handleChange}
                                maxLength={64}
                                dataQa="account-phone-book-name-field"
                            />
                            
                            <SelectField
                                dataQa="account-phone-book-phone-type-field"
                                label={t('screens:accountPhoneBook.type')}
                                items={types}
                                value={types.find(
                                    (v) => v.value === values.phone_type,
                                )}
                                getOptionLabel={(v: { name: string; value: string }) => v.name}
                                getOptionSelected={(v: { name: string; value: string }) =>
                                    v.value === values.phone_type
                                }
                                onChange={(_, item: DropDownDictionaryItem<string>) => {
                                    setFieldValue('phone_type', item.value);
                                }}
                                disableClearable
                                required
                                disabled={values.lock_type == 'Number'}
                            />

                            <SwitchWithLabel
                                label={t('screens:accountPhoneBook.favoriteNumber')}
                                setValue={(field, value) => {
                                    setFieldValue(field, value ? YesNo.Yes : YesNo.No);
                                }}
                                value={values.favorite_rating == YesNo.Yes}
                                field={'favorite_rating'}
                                id={'favorite_rating'}
                                className={classes.favoriteNumber}
                                icon={
                                    <IconWithTooltip
                                        dataQa="favoriteNumber-tooltip"
                                        tooltipText={t('tooltips:accountPhoneBook.whenYouMarkAPhoneNumberAsAFavorite')}
                                    />
                                }
                                disabled={
                                    ((errors.favorite_rating?.length ?? 0) > 0) || (values.lock_type == 'Number')
                                }
                            />
                        </Box>
                    </Grid>
                </form>
            </DialogContainer>
        </PermissionProvider>
    );
};

export default NewAccountPhoneBookEntryDialog;
